import { useState, useEffect } from 'react'
import { 
  collection, 
  doc, 
  setDoc, 
  deleteDoc,
  updateDoc,
  query, 
  orderBy, 
  onSnapshot,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from '../contexts/AuthContext'
import { 
  Artist, 
  ArtistFormData
} from '../types/artist'
import toast from 'react-hot-toast'

export const useArtists = () => {
  const [artists, setArtists] = useState<Artist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const { user } = useAuth()

  useEffect(() => {
    const artistsRef = collection(db, 'artists')
    // Simplified query to avoid index requirement
    const q = query(artistsRef, orderBy('createdAt', 'desc'))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const artistsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as Artist[]

        // Sort by order field in memory since we can't use composite index yet
        const sortedArtists = artistsData.sort((a, b) => (a.order || 0) - (b.order || 0))
        setArtists(sortedArtists)
        setLoading(false)
        setError(null)
      },
      (error) => {
        console.error('Error fetching artists:', error)
        setError('Failed to load artists')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [])

  const createArtist = async (artistData: ArtistFormData): Promise<string | null> => {
    if (!user) {
      toast.error('You must be logged in to create artists')
      return null
    }

    try {
      const artistId = `artist_${Date.now()}`
      const newArtist: Omit<Artist, 'id'> = {
        ...artistData,
        order: artists.length,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: user.email || 'Unknown',
        updatedBy: user.email || 'Unknown'
      }

      await setDoc(doc(db, 'artists', artistId), {
        ...newArtist,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      })

      toast.success('Artist created successfully!')
      return artistId
    } catch (error) {
      console.error('Error creating artist:', error)
      toast.error('Failed to create artist')
      return null
    }
  }

  const updateArtist = async (artistId: string, artistData: Partial<ArtistFormData>): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to update artists')
      return false
    }

    try {
      await updateDoc(doc(db, 'artists', artistId), {
        ...artistData,
        updatedAt: serverTimestamp(),
        updatedBy: user.email || 'Unknown'
      })

      toast.success('Artist updated successfully!')
      return true
    } catch (error) {
      console.error('Error updating artist:', error)
      toast.error('Failed to update artist')
      return false
    }
  }

  const deleteArtist = async (artistId: string): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to delete artists')
      return false
    }

    try {
      await deleteDoc(doc(db, 'artists', artistId))
      toast.success('Artist deleted successfully!')
      return true
    } catch (error) {
      console.error('Error deleting artist:', error)
      toast.error('Failed to delete artist')
      return false
    }
  }

  const toggleFeatured = async (artistId: string, featured: boolean): Promise<boolean> => {
    return updateArtist(artistId, { featured })
  }

  const toggleActive = async (artistId: string, active: boolean): Promise<boolean> => {
    return updateArtist(artistId, { active })
  }

  const reorderArtists = async (reorderedArtists: Artist[]): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to reorder artists')
      return false
    }

    try {
      const batch = writeBatch(db)
      
      reorderedArtists.forEach((artist, index) => {
        const artistRef = doc(db, 'artists', artist.id)
        batch.update(artistRef, { 
          order: index,
          updatedAt: serverTimestamp(),
          updatedBy: user.email || 'Unknown'
        })
      })

      await batch.commit()
      toast.success('Artists reordered successfully!')
      return true
    } catch (error) {
      console.error('Error reordering artists:', error)
      toast.error('Failed to reorder artists')
      return false
    }
  }

  const bulkUpdateArtists = async (artistIds: string[], updates: Partial<ArtistFormData>): Promise<boolean> => {
    if (!user) {
      toast.error('You must be logged in to update artists')
      return false
    }

    try {
      const batch = writeBatch(db)
      
      artistIds.forEach(artistId => {
        const artistRef = doc(db, 'artists', artistId)
        batch.update(artistRef, {
          ...updates,
          updatedAt: serverTimestamp(),
          updatedBy: user.email || 'Unknown'
        })
      })

      await batch.commit()
      toast.success(`${artistIds.length} artists updated successfully!`)
      return true
    } catch (error) {
      console.error('Error bulk updating artists:', error)
      toast.error('Failed to update artists')
      return false
    }
  }

  return {
    artists,
    loading,
    error,
    createArtist,
    updateArtist,
    deleteArtist,
    toggleFeatured,
    toggleActive,
    reorderArtists,
    bulkUpdateArtists
  }
}

export const useArtist = (artistId: string) => {
  const [artist, setArtist] = useState<Artist | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!artistId) {
      setLoading(false)
      return
    }

    const artistRef = doc(db, 'artists', artistId)
    
    const unsubscribe = onSnapshot(
      artistRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const artistData = {
            id: snapshot.id,
            ...snapshot.data(),
            createdAt: snapshot.data().createdAt?.toDate() || new Date(),
            updatedAt: snapshot.data().updatedAt?.toDate() || new Date(),
          } as Artist

          setArtist(artistData)
        } else {
          setArtist(null)
        }
        setLoading(false)
        setError(null)
      },
      (error) => {
        console.error('Error fetching artist:', error)
        setError('Failed to load artist')
        setLoading(false)
      }
    )

    return () => unsubscribe()
  }, [artistId])

  return { artist, loading, error }
}

// Hook for public site to fetch artists
export const usePublicArtists = () => {
  const [artists, setArtists] = useState<Artist[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const artistsRef = collection(db, 'artists')
    // Simplified query - we'll filter active status in the component
    const q = query(artistsRef, orderBy('order', 'asc'))

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const artistsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate() || new Date(),
          updatedAt: doc.data().updatedAt?.toDate() || new Date(),
        })) as Artist[]

        setArtists(artistsData)
        setLoading(false)
        setError(null)
      },
      (error) => {
        console.error('Error fetching public artists:', error)
        // Fallback to empty array on error for public site
        setArtists([])
        setLoading(false)
        setError('Using fallback data')
      }
    )

    return () => unsubscribe()
  }, [])

  return { artists, loading, error }
}
